import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button, ErrorNote, Field, Input } from '@/components/ui';
import { api } from '@/lib/api';
import { saveSession, type Session } from '@/lib/session';

export const Login = ({ onSignedIn }: { onSignedIn: () => void }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<{ name: string; session: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const done = (session: Session) => {
    saveSession(session);
    void qc.invalidateQueries();
    onSignedIn();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (challenge) {
        const res = await api<{ session: Session }>('/v1/auth/challenge', { body: { email, code, session: challenge.session, challengeName: challenge.name }, auth: false });
        done(res.session);
      } else {
        const res = await api<{ session?: Session; challenge?: { name: string; session: string } }>('/v1/auth/sign-in', { body: { email, password }, auth: false });
        if (res.challenge) setChallenge(res.challenge);
        else if (res.session) done(res.session);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-lg border border-line bg-panel p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="size-6 rounded-md bg-ink p-1.5">
            <span className="block size-full rounded-full bg-cyan-400" />
          </span>
          <h1 className="text-[17px] font-semibold">Agent Manager</h1>
        </div>
        <div className="flex flex-col gap-3">
          {!challenge ? (
            <>
              <Field label="Email">
                <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </Field>
              <Field label="Password">
                <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
            </>
          ) : (
            <Field label={challenge.name === 'EMAIL_OTP' ? 'We emailed you a code' : 'Authentication code'}>
              <Input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
            </Field>
          )}
          {error ? <ErrorNote error={error} /> : null}
          <Button variant="primary" type="submit" loading={busy} className="mt-1">
            {challenge ? 'Verify' : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  );
};
